<p-toast></p-toast>

<div class="cs-page">

  <!-- ===== Page Header ===== -->
  <div class="cs-page-header">
    <div class="cs-page-header-left">
      <h1 class="cs-page-title">{{ template.templateName }}</h1>
      <nav class="cs-breadcrumb">
        <button class="cs-bc-link" type="button" (click)="onAllTemplates()">Templates</button>
        <i class="ri-arrow-right-s-line"></i>
        <span class="cs-bc-current">{{ template.templateName }}</span>
      </nav>
    </div>
    <div class="cs-page-actions">
      <button class="cs-btn-secondary" type="button" (click)="onAllTemplates()">
        <i class="ri-arrow-left-line"></i>All Templates
      </button>
      @if (canCreate) {
        <button class="cs-btn-primary" type="button" (click)="openSectionDialog()" [disabled]="isProjectInactive">
          <i class="ri-add-line"></i>New Section
        </button>
      }
    </div>
  </div>

  <!-- ===== Info Card ===== -->
  <div class="cs-info-card">
    <div class="cs-info-body">
      <p class="cs-info-name">{{ template.templateName }}</p>
      <p class="cs-info-desc">{{ template.description }}</p>
    </div>
    <span class="cs-info-date">{{ (template.createdAt ?? template.updatedAt) | date:'MMM d, yyyy | h:mm a' }}</span>
  </div>

  <!-- ===== Sections ===== -->
  <div class="cs-sections">

    <!-- Recursive section/subsection template -->
    <ng-template #sectionTpl let-sect="section" let-level="level" let-siblings="siblings" let-siblingIndex="siblingIndex">
      <div class="cs-section" [class.cs-section--expanded]="sect.isExpanded" [class.cs-section--sub]="level > 0">

        <!-- Section header row -->
        <div class="cs-section-hdr" (click)="toggleSection(sect)">
          <button class="cs-toggle-btn" type="button" (click)="$event.stopPropagation(); toggleSection(sect)">
            <i [class]="sect.isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
          </button>
          <div class="cs-section-info">
            <span class="cs-section-name">{{ sect.sectionName }}</span>
            @if (sect.description) {
              <span class="cs-section-desc" [class.cs-section-desc--truncated]="!sect.isExpanded" [title]="!sect.isExpanded ? sect.description : null">{{ sect.description }}</span>
            }
          </div>
          <div class="cs-section-acts" (click)="$event.stopPropagation()">
            @if (canUpdate) {
              <button class="cs-icon-btn" type="button" (click)="moveSectionUp(sect, siblings, siblingIndex)" [disabled]="isProjectInactive || siblingIndex === 0" title="Move up">
                <i class="ri-arrow-up-line"></i>
              </button>
              <button class="cs-icon-btn" type="button" (click)="moveSectionDown(sect, siblings, siblingIndex)" [disabled]="isProjectInactive || siblingIndex === siblings.length - 1" title="Move down">
                <i class="ri-arrow-down-line"></i>
              </button>
              <button class="cs-icon-btn" type="button" (click)="openEditDialog(sect)" title="Edit section"
                [disabled]="isProjectInactive">
                <i class="ri-pencil-line"></i>
              </button>
            }
            @if (canDelete) {
              <button class="cs-icon-btn cs-icon-btn--danger" type="button" (click)="deleteSection(sect)"
                title="Delete section" [disabled]="isProjectInactive">
                <i class="ri-delete-bin-line"></i>
              </button>
            }
          </div>
        </div>

        <!-- Expanded body -->
        @if (sect.isExpanded) {
          <div class="cs-section-body">

            @for (child of getSectionChildren(sect); track $index; let isFirst = $first; let isLast = $last) {
              @if (child.type === 'lineItem') {
                <!-- Line item -->
                <div class="cs-item">
                  <div class="cs-item-info">
                    <span class="cs-item-name">{{ asLineItem(child).name }}</span>
                    <div class="cs-item-meta">
                      <span class="cs-item-meta-lbl">Control Type</span>
                      <span class="cs-item-meta-val">{{ getControlTypeName(asLineItem(child).controlTypeId || 0) }}</span>
                    </div>
                    <div class="cs-item-req">
                      <span class="cs-chk" [class.cs-chk--on]="asLineItem(child).isRequired">
                        @if (asLineItem(child).isRequired) {
                          <i class="ri-check-line"></i>
                        }
                      </span>
                      <span>Required</span>
                    </div>
                  </div>
                  <div class="cs-item-acts">
                    @if (canUpdate) {
                      <button class="cs-icon-btn" type="button" (click)="moveChildUp(sect, $index)" [disabled]="isProjectInactive || isFirst" title="Move up">
                        <i class="ri-arrow-up-line"></i>
                      </button>
                      <button class="cs-icon-btn" type="button" (click)="moveChildDown(sect, $index)" [disabled]="isProjectInactive || isLast" title="Move down">
                        <i class="ri-arrow-down-line"></i>
                      </button>
                      <button class="cs-icon-btn" type="button" (click)="openEditLineItem(asLineItem(child))"
                        title="Edit item" [disabled]="isProjectInactive">
                        <i class="ri-pencil-line"></i>
                      </button>
                    }
                    @if (canDelete) {
                      <button class="cs-icon-btn cs-icon-btn--danger" type="button"
                        (click)="deleteLineItem(asLineItem(child))" title="Delete item" [disabled]="isProjectInactive">
                        <i class="ri-delete-bin-line"></i>
                      </button>
                    }
                  </div>
                </div>
                <div class="cs-divider"></div>
              } @else {
                <!-- Subsection (recursive) -->
                <ng-container *ngTemplateOutlet="sectionTpl; context: {section: asSubsection(child), level: level + 1, siblings: sect.subsections, siblingIndex: $index}"></ng-container>
              }
            }

            <!-- Section footer -->
            @if (canCreate) {
              <div class="cs-section-footer">
                <button class="cs-ghost-btn" type="button" (click)="openLineItemDialog(sect)" [disabled]="isProjectInactive">
                  <i class="ri-add-circle-fill"></i>New Item
                </button>
                @if (level === 0) {
                  <button class="cs-ghost-btn cs-ghost-btn--muted" type="button" (click)="openSubsectionDialog(sect)"
                    [disabled]="isProjectInactive">
                    <i class="ri-add-circle-line"></i>Add Subsection
                  </button>
                }
              </div>
            }

          </div>
        }

      </div>
    </ng-template>

    <!-- Render root sections -->
    @for (sect of sections; track $index) {
      <ng-container *ngTemplateOutlet="sectionTpl; context: {section: sect, level: 0, siblings: sections, siblingIndex: $index}"></ng-container>
    }

    <!-- Add Section CTA -->
    @if (canCreate) {
      <div class="cs-add-section" (click)="openSectionDialog()">
        <button class="cs-ghost-btn" type="button" [disabled]="isProjectInactive">
          <i class="ri-add-circle-line"></i>New Section
        </button>
      </div>
    }

  </div>
</div>

<!-- ===== Add / Edit Section Dialog ===== -->
<p-dialog
  [header]="isEdit ? 'Edit Section' : (currentParentSectionId ? 'Add Subsection' : 'Add Section')"
  [(visible)]="showSectionDialog"
  [modal]="true"
  [contentStyle]="{ overflow: 'auto' }"
  [style]="{ width: '30rem' }">
  <form [formGroup]="form" (ngSubmit)="saveSection()">
    <div class="grid">
      <div class="col-12">
        <label for="sectionNameInput" class="modal-label">Name <span class="req">*</span></label>
        <input id="sectionNameInput" type="text" pInputText formControlName="sectionName" class="w-full" />
      </div>
      <div class="col-12">
        <label for="sectionDescInput" class="modal-label">Description</label>
        <textarea id="sectionDescInput" pInputTextarea formControlName="description" class="w-full"></textarea>
      </div>
    </div>
    <div class="modal-footer">
      <button type="button" class="modal-btn-cancel" (click)="showSectionDialog = false">Cancel</button>
      <button type="submit" class="modal-btn-primary" [disabled]="form.invalid || !canSaveSection">Save</button>
    </div>
  </form>
</p-dialog>

<!-- ===== Add / Edit Line Item Dialog ===== -->
<p-dialog #lineItemDialog
  [header]="isLineItemEdit ? 'Edit Item' : 'Add Item'"
  [(visible)]="showChecklistDialog"
  (onHide)="resetLineItemForm()"
  (onShow)="lineItemDialog.maximize()"
  [modal]="true"
  [maximizable]="true"
  [contentStyle]="{ overflow: 'auto' }"
  [style]="{ width: '40rem' }">
  <ng-template pTemplate="content">
    <form [formGroup]="lineItemForm">
      <div class="grid">
        <div class="col-12">
          <label for="controlTypeId" class="modal-label">Control Type <span class="req">*</span></label>
          <select id="controlTypeId" formControlName="controlTypeId"
            class="w-full p-3 border-1 border-300 border-round surface-0 text-900"
            [disabled]="!canSaveLineItem">
            @for (option of controlTypeOptions; track $index) {
              <option [value]="option.controlTypeId">{{ option.controlTypeName }}</option>
            }
          </select>
          @if (+lineItemForm.get('controlTypeId')?.value === 22) {
            <small class="block mt-2 font-bold text-red-600">
              Email notifications will be sent to the respective impacted area holders.
            </small>
          }
        </div>
        <div class="col-12 mt-2">
          <label for="checklistName" class="modal-label">Name <span class="req">*</span></label>
          <textarea id="checklistName" class="w-full p-inputtext-lg" rows="5" pInputTextarea
            formControlName="name" [disabled]="!canSaveLineItem"></textarea>
        </div>
        @if ((loadingIntegrationAssociations || integrationCategoryOptions.length > 0) && lineItemForm.get('controlTypeId')?.value != 5) {
          <div class="col-12 mt-2">
            <label for="integrationCategory" class="modal-label">Integration Type</label>
            <p-select
              inputId="integrationCategory"
              [(ngModel)]="selectedIntegrationCategory"
              [ngModelOptions]="{ standalone: true }"
              [options]="integrationCategoryOptions"
              optionLabel="label"
              optionValue="value"
              placeholder="Select Integration Type"
              styleClass="w-full"
              (onChange)="onIntegrationCategoryChange()"
              [disabled]="!canSaveLineItem">
            </p-select>
          </div>
          <div class="col-12 mt-2">
            <label for="integrationMappingId" class="modal-label">Integration Data Mapping</label>
            <p-select
              inputId="integrationMappingId"
              formControlName="integrationMappingId"
              [options]="filteredIntegrationAssociations"
              [optionLabel]="integrationOptionLabel"
              optionValue="integrationMappingId"
              placeholder="None"
              [showClear]="true"
              [filter]="true"
              [filterBy]="integrationFilterBy"
              [scrollHeight]="'300px'"
              appendTo="body"
              panelStyleClass="integration-map-panel"
              styleClass="w-full"
              [disabled]="!canSaveLineItem">
              <ng-template pTemplate="item" let-mapping>
                <span>{{ getIntegrationAssociationLabel(mapping) }}</span>
              </ng-template>
              <ng-template pTemplate="selectedItem" let-mapping>
                @if (mapping) {
                  <span>{{ getIntegrationAssociationLabel(mapping) }}</span>
                } @else {
                  <span class="text-500">None</span>
                }
              </ng-template>
            </p-select>
            @if (loadingIntegrationAssociations) {
              <small class="text-500">Loading mappings...</small>
            }
          </div>
        }
        @if (lineItemForm.get('controlTypeId')?.value == 1 || lineItemForm.get('controlTypeId')?.value == 2) {
          <div class="col-12 mt-2">
            <label for="defaultText" class="modal-label">Default Text (Optional)</label>
            <textarea id="defaultText" class="w-full p-inputtext-lg" rows="3" pInputTextarea
              formControlName="defaultText" placeholder="Enter default text to pre-fill this field"
              [disabled]="!canSaveLineItem"></textarea>
          </div>
        }
        @if (lineItemForm.get('controlTypeId')?.value == 14) {
          <div class="col-12 mt-2">
            <label for="labelText" class="modal-label">Formatted Label Text</label>
            <p-editor [style]="{ height: '200px' }" formControlName="defaultText" [readonly]="!canSaveLineItem">
              <ng-template pTemplate="header">
                <span class="ql-formats">
                  <button type="button" class="ql-bold" aria-label="Bold"></button>
                  <button type="button" class="ql-italic" aria-label="Italic"></button>
                  <button type="button" class="ql-underline" aria-label="Underline"></button>
                </span>
                <span class="ql-formats">
                  <select class="ql-color"></select>
                  <select class="ql-background"></select>
                </span>
                <span class="ql-formats">
                  <button type="button" class="ql-list" value="ordered" aria-label="Ordered List"></button>
                  <button type="button" class="ql-list" value="bullet" aria-label="Bullet List"></button>
                </span>
                <span class="ql-formats">
                  <select class="ql-align"></select>
                </span>
              </ng-template>
            </p-editor>
          </div>
        }
        @if (+lineItemForm.get('controlTypeId')?.value === 8) {
          <div class="col-12 mt-2">
            <label class="modal-label">Reference Images</label>
            <small class="block text-500 mb-2">These images will be shown as guidance when users fill this picture control.</small>
            <input type="file" accept="image/*,.heic,.heif" multiple (change)="onReferenceImagesSelected($event)"
              [disabled]="!canSaveLineItem" />
            @if (referenceImages.length > 0 || pendingReferenceImagePreviews.length > 0) {
              <div class="flex flex-wrap gap-3 mt-3">
                @for (image of referenceImages; track image.id; let idx = $index) {
                  <div class="border-1 border-300 border-round p-2 surface-50" style="width: 140px;">
                    <img [src]="getReferenceImageUrl(image)" [alt]="image.fileName || 'Reference image'"
                      class="w-full border-round mb-2" style="height: 90px; object-fit: cover;" />
                    <div class="text-sm text-700 white-space-nowrap overflow-hidden text-overflow-ellipsis mb-2">{{ image.fileName || image.id }}</div>
                    <button type="button" class="modal-btn-cancel w-full" (click)="removeExistingReferenceImage(idx)"
                      [disabled]="!canSaveLineItem">Remove</button>
                  </div>
                }
                @for (preview of pendingReferenceImagePreviews; track preview.objectUrl; let idx = $index) {
                  <div class="border-1 border-dashed border-400 border-round p-2 surface-0" style="width: 140px;">
                    <img [src]="preview.objectUrl" [alt]="preview.fileName"
                      class="w-full border-round mb-2" style="height: 90px; object-fit: cover;" />
                    <div class="text-sm text-700 white-space-nowrap overflow-hidden text-overflow-ellipsis mb-2">{{ preview.fileName }}</div>
                    <button type="button" class="modal-btn-cancel w-full" (click)="removePendingReferenceImage(idx)"
                      [disabled]="!canSaveLineItem">Remove</button>
                  </div>
                }
              </div>
            }
          </div>
        }
        @if ([4, 22].includes(+lineItemForm.get('controlTypeId')?.value)) {
          <div class="col-12">
            <label class="modal-label">{{ +lineItemForm.get('controlTypeId')?.value === 22 ? 'Impacted Area Options (Fixed)' : 'Add Options' }}</label>
            @if (+lineItemForm.get('controlTypeId')?.value !== 22) {
              <div class="col-12 flex align-items-center gap-3">
                <label class="modal-label">Option Text</label>
                <input class="p-inputtext-lg" type="text" pInputText [(ngModel)]="optionText" name="optionText"
                  [ngModelOptions]="{ standalone: true }" [disabled]="!canSaveLineItem" />
                <p-button label="Add" icon="pi pi-plus" [disabled]="!canSaveLineItem || !optionText"
                  (click)="addOptionText()" size="small" />
              </div>
            }
            @if (itemOptionValueList.length > 0) {
              <div class="col-12">
                <label class="modal-label">Options:</label>
                <ul>
                  @for (option of itemOptionValueList; track $index) {
                    <li class="flex justify-content-between align-items-center p-1">
                      <input class="p-inputtext-lg" type="text" pInputText [(ngModel)]="option.valueText"
                        name="optionText" [ngModelOptions]="{ standalone: true }"
                        [readonly]="+lineItemForm.get('controlTypeId')?.value === 22"
                        [disabled]="!canSaveLineItem" />
                      @if (+lineItemForm.get('controlTypeId')?.value !== 22) {
                        <p-button icon="pi pi-times" [text]="true" size="small" severity="danger"
                          (click)="removeOptionText($index)" [disabled]="!canSaveLineItem" />
                      }
                    </li>
                  }
                </ul>
              </div>
            }
          </div>
        }
        @if (lineItemForm.get('controlTypeId')?.value == 5) {
          <div class="col-12">
            <label class="modal-label">Add Columns (Max 9)</label>
            @if (loadingIntegrationAssociations || integrationCategoryOptions.length > 0) {
              <div class="col-12 flex align-items-center gap-3">
                <input id="columnName" placeholder="Column Name" [(ngModel)]="columnText"
                  [ngModelOptions]="{ standalone: true }" name="columnText" class="w-full p-inputtext-lg"
                  type="text" pInputText [disabled]="!canSaveLineItem" />
                <select id="dataTypeId" [(ngModel)]="selectedDataTypeId" [ngModelOptions]="{ standalone: true }"
                  (ngModelChange)="onDataTypeChange()" (change)="onDataTypeChange()"
                  class="w-full p-3 border-1 border-300 border-round surface-0 text-900"
                  [disabled]="!canSaveLineItem">
                  @for (option of tableDataTypeOptions; track $index) {
                    <option [value]="option.dataTypeId">{{ option.dataTypeName }}</option>
                  }
                </select>
              </div>
              <div class="col-12">
                <label for="columnIntegrationCategory" class="modal-label">Integration Type</label>
                <p-select
                  inputId="columnIntegrationCategory"
                  [(ngModel)]="selectedIntegrationCategory"
                  [ngModelOptions]="{ standalone: true }"
                  [options]="integrationCategoryOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Select Integration Type"
                  styleClass="w-full"
                  (onChange)="onIntegrationCategoryChange()"
                  [disabled]="!canSaveLineItem">
                </p-select>
              </div>
              <div class="col-12 flex align-items-center gap-3">
                <p-select
                  inputId="columnIntegrationMappingId"
                  [(ngModel)]="selectedColumnIntegrationMappingId"
                  [ngModelOptions]="{ standalone: true }"
                  name="columnIntegrationMappingId"
                  [options]="filteredIntegrationAssociations"
                  [optionLabel]="integrationOptionLabel"
                  optionValue="integrationMappingId"
                  placeholder="Integration Data Mapping"
                  [showClear]="true"
                  [filter]="true"
                  [filterBy]="integrationFilterBy"
                  [scrollHeight]="'300px'"
                  appendTo="body"
                  panelStyleClass="integration-map-panel"
                  styleClass="w-full"
                  (onChange)="onColumnIntegrationMappingChange()"
                  [disabled]="!canSaveLineItem">
                  <ng-template pTemplate="item" let-mapping>
                    <span>{{ getIntegrationAssociationLabel(mapping) }}</span>
                  </ng-template>
                  <ng-template pTemplate="selectedItem" let-mapping>
                    @if (mapping) {
                      <span>{{ getIntegrationAssociationLabel(mapping) }}</span>
                    } @else {
                      <span class="text-500">Integration Data Mapping</span>
                    }
                  </ng-template>
                </p-select>
                <div class="flex align-items-center gap-2">
                  <p-checkbox id="columnIsRequired" binary="true" [(ngModel)]="columnIsRequired"
                    [ngModelOptions]="{ standalone: true }" [disabled]="!canSaveLineItem"></p-checkbox>
                  <label for="columnIsRequired" class="font-medium text-900 mb-0">Required</label>
                </div>
                <p-button [label]="editingTableColumn ? 'Update' : 'Add'" [icon]="editingTableColumn ? 'pi pi-check' : 'pi pi-plus'"
                  [disabled]="!canSaveLineItem || !columnText || (!editingTableColumn && getVisibleTableColumnCount() >= 9)"
                  (click)="addTableColumn()" />
              </div>
            } @else {
              <div class="col-12 flex align-items-center gap-3">
                <input id="columnName" placeholder="Column Name" [(ngModel)]="columnText"
                  [ngModelOptions]="{ standalone: true }" name="columnText" class="w-full p-inputtext-lg"
                  type="text" pInputText [disabled]="!canSaveLineItem" />
                <select id="dataTypeId" [(ngModel)]="selectedDataTypeId" [ngModelOptions]="{ standalone: true }"
                  (ngModelChange)="onDataTypeChange()" (change)="onDataTypeChange()"
                  class="w-full p-3 border-1 border-300 border-round surface-0 text-900"
                  [disabled]="!canSaveLineItem">
                  @for (option of tableDataTypeOptions; track $index) {
                    <option [value]="option.dataTypeId">{{ option.dataTypeName }}</option>
                  }
                </select>
                <div class="flex align-items-center gap-2">
                  <p-checkbox id="columnIsRequiredNoMap" binary="true" [(ngModel)]="columnIsRequired"
                    [ngModelOptions]="{ standalone: true }" [disabled]="!canSaveLineItem"></p-checkbox>
                  <label for="columnIsRequiredNoMap" class="font-medium text-900 mb-0">Required</label>
                </div>
                <p-button [label]="editingTableColumn ? 'Update' : 'Add'" [icon]="editingTableColumn ? 'pi pi-check' : 'pi pi-plus'"
                  [disabled]="!canSaveLineItem || !columnText || (!editingTableColumn && getVisibleTableColumnCount() >= 9)"
                  (click)="addTableColumn()" />
              </div>
            }
            @if (shouldShowColumnOptions) {
              <div class="col-12">
                <label class="modal-label">Add Column Options</label>
                <div class="col-12 flex align-items-center gap-3">
                  <label class="modal-label">Option Text</label>
                  <input class="p-inputtext-lg" type="text" pInputText [(ngModel)]="columnOptionText"
                    [ngModelOptions]="{ standalone: true }" [disabled]="!canSaveLineItem" />
                  <p-button label="Add" icon="pi pi-plus"
                    [disabled]="!canSaveLineItem || !columnOptionText.trim()"
                    (click)="addColumnOption()" size="small" />
                </div>
                @if (columnOptionValueList.length > 0) {
                  <div class="col-12 mt-2">
                    <label class="modal-label">Column Options:</label>
                    <ul class="list-none p-0 m-0">
                      @for (option of columnOptionValueList; track $index) {
                        <li class="flex justify-content-between align-items-center py-1 px-2 surface-100 border-round my-1">
                          <span>{{ option.valueText }}</span>
                          <p-button icon="pi pi-times" [text]="true" size="small" severity="danger"
                            (click)="removeColumnOption($index)" [disabled]="!canSaveLineItem" />
                        </li>
                      }
                    </ul>
                  </div>
                }
              </div>
            }
            @if (getVisibleTableColumnCount() > 0) {
              <div class="col-12 mt-2">
                <label class="modal-label">Columns:</label>
                <ul>
                  @for (column of getVisibleTableColumns(); track $index) {
                    <li class="flex justify-content-between align-items-center">
                      <span>{{ column.columnName }} ({{ getTableDataTypeName(column.dataTypeId || 0) }})
                        @if (column.integrationMappingId) {
                          - {{ getIntegrationAssociationLabelById(column.integrationMappingId || null) }}
                        }
                        @if (column.isRequired) {
                          <span class="req">*</span>
                        }
                      </span>
                      <div class="flex align-items-center gap-1">
                        <p-button icon="pi pi-pencil" [text]="true" size="small"
                          (click)="editTableColumn(column)" [disabled]="!canSaveLineItem" />
                        <p-button icon="pi pi-times" [text]="true" size="small" severity="danger"
                          (click)="removeTableColumn(column)" [disabled]="!canSaveLineItem" />
                      </div>
                    </li>
                  }
                </ul>
              </div>
            }
          </div>
        }
        <!-- Validation Config: TextBox / TextArea -->
        @if ([1, 2].includes(+lineItemForm.get('controlTypeId')?.value)) {
          <div class="col-12">
            <label class="modal-label">Validation Rules</label>
            <div class="grid formgrid row-gap-2">
              <div class="col-6">
                <label class="modal-label">Min Length</label>
                <input type="number" pInputText class="w-full" formControlName="vcMinLength"
                  min="0" [disabled]="!canSaveLineItem" placeholder="e.g. 5" />
              </div>
              <div class="col-6">
                <label class="modal-label">Max Length</label>
                <input type="number" pInputText class="w-full" formControlName="vcMaxLength"
                  min="0" [disabled]="!canSaveLineItem" placeholder="e.g. 200" />
              </div>
              <div class="col-12">
                <label class="modal-label">Pattern (Regex)</label>
                <input type="text" pInputText class="w-full" formControlName="vcPattern"
                  [disabled]="!canSaveLineItem" placeholder="e.g. ^[A-Z]" />
              </div>
              <div class="col-12">
                <label class="modal-label">Pattern Error Message</label>
                <input type="text" pInputText class="w-full" formControlName="vcPatternMessage"
                  [disabled]="!canSaveLineItem" placeholder="e.g. Must start with uppercase letter" />
              </div>
            </div>
          </div>
        }

        <!-- Validation Config: Integer / Decimal -->
        @if ([16, 17].includes(+lineItemForm.get('controlTypeId')?.value)) {
          <div class="col-12">
            <label class="modal-label">Validation Rules</label>
            <div class="grid formgrid row-gap-2">
              <div class="col-6">
                <label class="modal-label">Min Value</label>
                <input type="number" pInputText class="w-full" formControlName="vcMin"
                  [disabled]="!canSaveLineItem" placeholder="e.g. 0" />
              </div>
              <div class="col-6">
                <label class="modal-label">Max Value</label>
                <input type="number" pInputText class="w-full" formControlName="vcMax"
                  [disabled]="!canSaveLineItem" placeholder="e.g. 100" />
              </div>
            </div>
          </div>
        }

        <!-- Validation Config: Picture / Attachment / Signature -->
        @if ([7, 8, 9].includes(+lineItemForm.get('controlTypeId')?.value)) {
          <div class="col-12">
            <label class="modal-label">Validation Rules</label>
            <div class="grid formgrid row-gap-2">
              <div class="col-6">
                <label class="modal-label">Min Files</label>
                <input type="number" pInputText class="w-full" formControlName="vcMinCount"
                  min="0" [disabled]="!canSaveLineItem" placeholder="e.g. 1" />
              </div>
              <div class="col-6">
                <label class="modal-label">Max Files</label>
                <input type="number" pInputText class="w-full" formControlName="vcMaxCount"
                  min="0" [disabled]="!canSaveLineItem" placeholder="e.g. 5" />
              </div>
            </div>
          </div>
        }

        <!-- Validation Config: DatePicker / DateTimePicker -->
        @if ([6, 19].includes(+lineItemForm.get('controlTypeId')?.value)) {
          <div class="col-12">
            <label class="modal-label">Validation Rules</label>
            <div class="grid formgrid row-gap-2">
              <div class="col-6">
                <label class="modal-label">Min Date (ISO or "today")</label>
                <input type="text" pInputText class="w-full" formControlName="vcMinDate"
                  [disabled]="!canSaveLineItem" placeholder="e.g. 2025-01-01 or today" />
              </div>
              <div class="col-6">
                <label class="modal-label">Max Date (ISO or "today")</label>
                <input type="text" pInputText class="w-full" formControlName="vcMaxDate"
                  [disabled]="!canSaveLineItem" placeholder="e.g. 2025-12-31 or today" />
              </div>
            </div>
          </div>
        }

        <!-- Validation Config: Table -->
        @if (+lineItemForm.get('controlTypeId')?.value === 5) {
          <div class="col-12">
            <label class="modal-label">Row Count Validation</label>
            <div class="grid formgrid row-gap-2">
              <div class="col-6">
                <label class="modal-label">Min Rows</label>
                <input type="number" pInputText class="w-full" formControlName="vcMinRows"
                  min="0" [disabled]="!canSaveLineItem" placeholder="e.g. 1" />
              </div>
              <div class="col-6">
                <label class="modal-label">Max Rows</label>
                <input type="number" pInputText class="w-full" formControlName="vcMaxRows"
                  min="0" [disabled]="!canSaveLineItem" placeholder="e.g. 20" />
              </div>
            </div>
          </div>
        }

        <div class="col-12 flex align-items-center gap-3">
          <label for="isRequired" class="modal-label mb-0">Is Required</label>
          <p-checkbox id="isRequired" binary="true" formControlName="isRequired"
            [disabled]="!canSaveLineItem"></p-checkbox>
        </div>
      </div>
    </form>
  </ng-template>
  <ng-template pTemplate="footer">
    <div class="modal-footer">
      <button type="button" class="modal-btn-cancel" (click)="showChecklistDialog = false">Cancel</button>
      <button type="button" class="modal-btn-primary" [disabled]="lineItemForm.invalid || !canSaveLineItem"
        (click)="saveLineItem()">Save</button>
    </div>
  </ng-template>
</p-dialog>
